import type { Observable } from 'rxjs';

import { isEmpty } from 'lodash-es';
import { useElementSize } from '../../hooks';
import type { MarketDataSnapshot } from '../../types/MarketDataSnapshot';
import type { OHLCData } from '../../types/OHLCData';
import type { ResolutionEnumNoPrefix } from '../../types/OrderAnalytic';
import type { Security } from '../../types/Security';
import { SmartTruncateEnum } from '../FormattedNumber';
import { LoaderWrapper } from '../LoaderTalos';
import { Spinner } from '../Spinner';
import { Settings } from './Settings';
import { VolumeLadderWrapper } from './styles';
import type { TopOfBookProps } from './TopOfBook';
import type { ClickRowCallback } from './types';
import { HEIGHT_LIMIT, VolumeLadderContent } from './VolumeLadderContent';
import { VolumeLadderHeader } from './VolumeLadderHeader';

const WIDTH_LIMIT = 320;

export const VolumeLadder = ({
  resolution,
  marketViewObservable,
  marketDataObservable,
  sparklineDataObservable,
  security,
  sizeBuckets,
  showSettings,
  showSpread,
  smartTruncate = SmartTruncateEnum.None,
  errors,
  disabledSymbolSelector,
  disableCustomLadderSizeBuckets,
  showDisclaimer,
  setErrors,
  onSaveSettings,
  onSettingsClick,
  onClickRow,
  onSymbolChange,
  onChangeResolution,
  onChangeSizeBuckets,
  onResetSizeBuckets,
  bpsIncrement,
  usePreciseTopOfBookIncrement,
  hideHeader,
}: VolumeLadderProps) => {
  const { size, elementRef } = useElementSize<HTMLDivElement>();

  return (
    <VolumeLadderWrapper ref={elementRef}>
      {
        // To avoid changing between components on mount, we do the first render when size is known.
        isEmpty(size) ? (
          <LoaderWrapper>
            <Spinner />
          </LoaderWrapper>
        ) : (
          <>
            {hideHeader === true ? null : (
              <VolumeLadderHeader
                errors={errors}
                showSettings={showSettings}
                symbol={security?.Symbol}
                onSaveSettings={onSaveSettings}
                onSymbolChange={onSymbolChange}
                onSettingsClick={onSettingsClick}
                disabledSymbolSelector={disabledSymbolSelector}
                isCompact={(size.offsetHeight ?? 0) < HEIGHT_LIMIT}
                isNarrow={(size.offsetWidth ?? 0) < WIDTH_LIMIT}
                disableCustomLadderSizeBuckets={disableCustomLadderSizeBuckets}
                showDisclaimer={showDisclaimer}
              />
            )}
            <VolumeLadderContent
              resolution={resolution}
              onClickRow={onClickRow}
              security={security}
              marketViewObservable={marketViewObservable}
              sparklineDataObservable={sparklineDataObservable}
              marketDataObservable={marketDataObservable}
              showSpread={showSpread}
              sizeBuckets={sizeBuckets}
              smartTruncate={smartTruncate}
              size={size}
              bpsIncrement={bpsIncrement}
              usePreciseTopOfBookIncrement={usePreciseTopOfBookIncrement}
            />
            {showSettings && security != null && (
              <Settings
                showSettings={showSettings}
                resolution={resolution}
                sizeBuckets={sizeBuckets}
                onChangeResolution={onChangeResolution}
                onChangeSizeBuckets={onChangeSizeBuckets}
                onResetSizeBuckets={onResetSizeBuckets}
                security={security}
                errors={errors}
                setErrors={setErrors}
              />
            )}
          </>
        )
      }
    </VolumeLadderWrapper>
  );
};

export type VolumeLadderProps = {
  resolution: ResolutionEnumNoPrefix;
  sparklineDataObservable: Observable<OHLCData[]>;
  marketViewObservable: Observable<MarketDataSnapshot>;
  marketDataObservable: Observable<MarketDataSnapshot>;
  security?: Security;
  sizeBuckets: string[];
  showSettings: boolean;
  showSpread?: boolean;
  smartTruncate?: SmartTruncateEnum;
  errors: boolean[];
  disabledSymbolSelector?: boolean;
  setErrors(errors: boolean[]): void;
  onSettingsClick(): void;
  onSaveSettings(): void;
  onClickRow: ClickRowCallback;
  onSymbolChange: (symbol?: string) => void;
  onChangeResolution: (resolution: ResolutionEnumNoPrefix) => void;
  onChangeSizeBuckets: (sizeBuckets: string[]) => void;
  onResetSizeBuckets: () => void;
  bpsIncrement?: string;
  disableCustomLadderSizeBuckets?: boolean;
  showDisclaimer?: boolean;
  usePreciseTopOfBookIncrement?: TopOfBookProps['usePreciseTopOfBookIncrement'];
  hideHeader?: boolean;
};
